'use client';

import type React from 'react';
import type { JSX } from 'react';
import { forwardRef, useCallback } from 'react';
//import { useTrackArticlePreview } from '@//data/tracking/useTrackArticlePreview';
import { useViewportTracking } from '@//hooks/useViewportTracking';
import styles from './FeaturedCard.module.scss';

interface FeaturedCardImageProps {
  src: string;
  srcSet?: string;
  srcWebp?: string;
  alt: string;
}

interface FeaturedCardProps {
  image?: FeaturedCardImageProps;
  heading: string;
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  excerpt?: string;
  label?: string;
  logotype?: React.ReactNode;
  action?: React.ReactNode;
  fetchPriority?: React.ImgHTMLAttributes<HTMLImageElement>['fetchPriority'];
  articleId?: string;
  articleTitle?: string;
}

export const FeaturedCard = forwardRef<HTMLElement, FeaturedCardProps>(
  (
    {
      image,
      heading,
      headingLevel = 2,
      excerpt,
      label,
      logotype,
      action,
      fetchPriority = 'high',
      articleId,
      articleTitle,
    },
    ref,
  ) => {
    const HeadingTag = `h${headingLevel}` as keyof JSX.IntrinsicElements;
    //const { trackArticlePreview } = useTrackArticlePreview();

    const handleViewportEnter = useCallback(() => {
      if (articleId && articleTitle) {
        /*
        trackArticlePreview({
          id: articleId,
        });
        */
      }
    }, [/*trackArticlePreview, */ articleId, articleTitle]);

    const { elementRef } = useViewportTracking({
      onEnterViewport: handleViewportEnter,
      threshold: 0.5,
      trackOnce: true,
    });

    return (
      <article ref={ref || elementRef} className={styles.card}>
        {image && (
          <picture>
            {image.srcWebp && <source srcSet={image.srcWebp} type="image/webp" />}
            <img
              src={image.src}
              srcSet={image.srcSet}
              alt={image.alt}
              className={styles.image}
              fetchPriority={fetchPriority}
            />
          </picture>
        )}
        <div className={styles.content}>
          {label && (
            <span className={styles.label}>
              {logotype}
              {label}
            </span>
          )}
          <HeadingTag className={styles.heading}>{heading}</HeadingTag>
          {excerpt && <p className={styles.excerpt}>{excerpt}</p>}
          {action}
        </div>
      </article>
    );
  },
);

FeaturedCard.displayName = 'FeaturedCard';
